import React, { useContext, useEffect, useState } from "react";
import { Button, Form, Input, Select, Row, Col, AutoComplete } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { debounce } from "lodash";
import { Link } from "react-router-dom";
import { getUserInfo } from "../../utils/utils";
import { PAGE_LENGTH } from "../../config";
import {
  updateLeadDetails,
  resetLeadCreatedInfo,
} from "../../actionCreator/leadDetailsActionCreator";
import {
  getExecutiveEmployeeList,
  searchIndustry,
} from "../../actionCreator/leadListingActionCreater";
import { isValidEmail } from "../../utils";
import { useAlert } from "../../context/AlertProvider";
import CustomFields from "../leads-details/CustomFields";
import {
  formatAndExtractCountryCode,
  handleMobileChange,
  normalizeMobileNumber,
  prefixSelector,
  validateMobileNumber,
} from "../../utils/mobileValidation";
import { countries } from "../../constant/Countries";
import PlaceAutocomplete from "../../common/PlaceAutocomplete";
import { AuthContext } from "../../context/AuthContext";

const LeadForm = ({ setLoading, closeModal, initialValues, isNewLead }) => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const { isLeadEdit } = useContext(AuthContext);
  const [form] = Form.useForm();
  const [moreInfo, setMoreInfo] = useState(false);
  const [leadRatingList, setLeadRatingList] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [wtlCustomFields, setWtlCustomFields] = useState();

  const companySelectedFilterList = useSelector(
    (state) => state.leadListingReducer.selectedFilters
  );
  const leadRatingListing = useSelector(
    (state) => state.leadListingReducer.leadRatingList
  );
  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList
  );

  useEffect(() => {
    const ratingItem = leadRatingListing.map((item) => ({
      label: item.ratingText,
      value: item.ratingText,
    }));
    setLeadRatingList(ratingItem);
  }, [leadRatingListing]);

  useEffect(() => {
    if (initialValues?.id) {
      setMoreInfo(true);
      setTimeout(() => {
        const phoneNo = initialValues?.phoneNo?.split("/") || [];
        form.setFieldsValue({
          ...initialValues,
          status: leadStatusListing?.find(
            (i) => i?.id === initialValues?.status
          )?.text,
          phPrefix: formatAndExtractCountryCode(phoneNo[0])?.countryLabel,
          phoneNo: formatAndExtractCountryCode(phoneNo[0])
            ?.numberWithoutCountryCode,
          prefix: formatAndExtractCountryCode(initialValues?.mobile)
            ?.countryLabel,
          mobile: formatAndExtractCountryCode(initialValues?.mobile)
            ?.numberWithoutCountryCode,
        });
        setWtlCustomFields(initialValues?.wtlCustomFields);
      }, 0);
    } else {
      form.setFieldsValue({ phPrefix: "India", prefix: "India" });
    }
  }, [initialValues, form, leadStatusListing]);

  const updateLead = async (values) => {
    const payload = {
      ...values,
      phoneNo: values?.phoneNo
        ? "+" +
          countries?.find((d) => d?.label === values?.phPrefix)?.code +
          " " +
          values?.phoneNo
        : undefined,
      mobile: values?.mobile
        ? "+" +
          countries?.find((d) => d?.label === values?.prefix)?.code +
          " " +
          values?.mobile
        : undefined,
      fullname: `${values.firstname} ${values.lastname}`,
      userId: getUserInfo().id,
      industryId: industries?.find((d) => d?.name === values?.industryText)?.id,
      status: 11,
      wtlCustomFields,
    };

    setLoading && setLoading(true);

    const res = await dispatch(
      updateLeadDetails(
        initialValues?.id
          ? {
              ...initialValues,
              ...payload,
              status: leadStatusListing?.find((i) => i?.text === values?.status)
                ?.id,
            }
          : {
              ...payload,
            },
        !!initialValues?.id
      )
    );
    if (res?.response?.status) {
      if (res?.response?.status === 403) {
        showAlert(
          "warning",
          "Permission Denied",
          !initialValues?.id
            ? "You do not have permission to create lead."
            : "You do not have permission to update lead."
        );
        if (!initialValues?.id) {
          closeModal();
          form.resetFields();
        }
      } else {
        showAlert("error", res.response?.data?.title);
      }
    } else {
      if (!initialValues?.id) {
        showAlert("success", "Lead created successfully.");
        dispatch(resetLeadCreatedInfo());
        form.resetFields();

        const pageValues = { start: 0, end: PAGE_LENGTH };
        const payload = {
          ...companySelectedFilterList,
          selectedPageLayout: 1,
        };
        dispatch(getExecutiveEmployeeList(payload, pageValues));
        closeModal();
      } else {
        showAlert("success", "Lead updated successfully.");
      }
    }
    setLoading && setLoading(false);
  };

  const onSearch = debounce(async (value) => {
    const res = await dispatch(searchIndustry(value));
    setIndustries(res);
  }, 500);

  return (
    <Form
      form={form}
      onFinish={updateLead}
      className="lead-form"
      layout="vertical"
    >
      <div
        className="mt10"
        style={{
          padding: "0px 24px 0px",
          maxHeight: !initialValues?.id ? 500 : "auto",
          overflowY: "auto",
          marginRight: 24,
        }}
      >
        <Row gutter={[24, 0]}>
          <Col span={24} xl={isNewLead ? 24 : 12}>
            <Form.Item
              label={<label className="fw-medium">First Name</label>}
              name="firstname"
              className="mb15"
              rules={[{ required: true, message: "Please enter first name" }]}
            >
              <Input className="common-input" />
            </Form.Item>
          </Col>

          <Col span={24} xl={isNewLead ? 24 : 12}>
            <Form.Item
              label={<label className="fw-medium">Last Name</label>}
              name="lastname"
              className="mb15"
              rules={[{ required: true, message: "Please enter last name" }]}
            >
              <Input className="common-input" />
            </Form.Item>
          </Col>

          <Col span={24} xl={isNewLead ? 24 : 12}>
            <Form.Item
              label={<label className="fw-medium">Designation</label>}
              className="mb15"
              name="title"
            >
              <Input className="common-input" />
            </Form.Item>
          </Col>

          <Col span={24} xl={isNewLead ? 24 : 12}>
            <Form.Item
              label={<label className="fw-medium">Phone</label>}
              className="mb15"
              name="phoneNo"
              normalize={normalizeMobileNumber}
              rules={[
                {
                  validator: validateMobileNumber,
                },
              ]}
            >
              <Input
                className="common-input"
                addonBefore={prefixSelector("phPrefix")}
                maxLength={20}
                onChange={(e) => handleMobileChange(e.target.value, form)}
              />
            </Form.Item>
          </Col>

          <Col span={24} xl={isNewLead ? 24 : 12}>
            <Form.Item
              label={<label className="fw-medium">Mobile</label>}
              name="mobile"
              className="mb15"
              normalize={normalizeMobileNumber}
              rules={[
                {
                  required: true,
                  message: "Please enter mobile number",
                },
                {
                  validator: validateMobileNumber,
                },
              ]}
            >
              <Input
                className="common-input mobile-no"
                addonBefore={prefixSelector()}
                maxLength={20}
                onChange={(e) => handleMobileChange(e.target.value, form)}
              />
            </Form.Item>
          </Col>

          <Col span={24} xl={isNewLead ? 24 : 12}>
            <Form.Item
              label={<label className="fw-medium">Email</label>}
              name="emailId"
              className="mb15"
              rules={[
                {
                  required: true,
                  validator: (_, value) =>
                    isValidEmail(value)
                      ? Promise.resolve()
                      : Promise.reject("Please enter a valid email"),
                },
              ]}
            >
              <Input className="common-input" />
            </Form.Item>
          </Col>

          <Col span={24} xl={isNewLead ? 24 : 12}>
            <Form.Item
              label={<label className="fw-medium">Company Name</label>}
              name="companyName"
              className="mb15"
            >
              <PlaceAutocomplete
                onPlaceSelect={(v) => {
                  form.setFieldsValue({
                    companyName: v?.name,
                    website: v?.website,
                  });
                }}
              />
            </Form.Item>
          </Col>

          <Col
            span={24}
            xl={isNewLead ? 24 : 12}
            className="d-flex align-items-center gap-10"
          >
            <Form.Item
              label={<label className="fw-medium">Website</label>}
              name="website"
              className="mb15"
              style={{ width: "100%" }}
            >
              <Input className="common-input" />
            </Form.Item>
            {initialValues?.website && (
              <Link
                className="mb15"
                title="mb15"
                to={`http://${initialValues?.website}`}
                target="_blank"
              >
                <span className="text-black la  la-globe mr-2"></span>
              </Link>
            )}
          </Col>

          {moreInfo && (
            <>
              <Col span={24} xl={isNewLead ? 24 : 12}>
                <Form.Item
                  label={<label className="fw-medium">Address</label>}
                  name="address"
                  className="mb15"
                >
                  <Input.TextArea
                    rows={2}
                    maxLength={100}
                    className="common-input"
                  />
                </Form.Item>
              </Col>

              <Col span={24} xl={isNewLead ? 24 : 12}>
                <Form.Item
                  label={<label className="fw-medium">Industry</label>}
                  name="industryText"
                  className="mb15"
                >
                  <AutoComplete
                    options={industries?.map((d) => ({
                      label: d.name,
                      value: d.name,
                    }))}
                    onSearch={onSearch}
                    allowClear
                    placeholder="Start typing a industry name"
                    style={{ border: "none" }}
                  >
                    <Input
                      className="common-input"
                      style={{
                        border: "none",
                        padding: "4.2px 11px 4.2px 11px",
                        fontSize: 12,
                        height: "100%",
                      }}
                    />
                  </AutoComplete>
                </Form.Item>
              </Col>

              <Col span={24} xl={isNewLead ? 24 : 12}>
                <Form.Item
                  label={
                    <label className="fw-medium">Potential Revenue (₹)</label>
                  }
                  name="rate"
                  className="mb15"
                  rules={[
                    {
                      pattern: /^\d+(\.\d{1,2})?$/,
                      message: "Please enter valid revenue",
                    },
                  ]}
                >
                  <Input className="common-input" />
                </Form.Item>
              </Col>

              <Col span={24} xl={isNewLead ? 24 : 12}>
                <Form.Item
                  label={
                    <label className="fw-medium">Opportunity Amount (₹)</label>
                  }
                  name="oppurtunityAmount"
                  className="mb15"
                  rules={[
                    {
                      pattern: /^\d+(\.\d{1,2})?$/,
                      message: "Please enter valid opportunity amount",
                    },
                  ]}
                >
                  <Input className="common-input" />
                </Form.Item>
              </Col>

              {!!initialValues?.id && (
                <Col span={24} xl={isNewLead ? 24 : 12}>
                  <Form.Item
                    label={<label className="fw-medium">Lead Status</label>}
                    name="status"
                    normalize={(value) => value?.replace(/^\s+/, "")}
                    rules={[
                      { required: true, message: "This field is required" },
                    ]}
                  >
                    <Select
                      placeholder="Select Status"
                      options={leadStatusListing?.map((d) => ({
                        label: d.text,
                        value: d.text,
                      }))}
                      onChange={(value) => {
                        form.setFieldsValue({
                          status: value,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
              )}

              <Col span={24} xl={isNewLead ? 24 : 12}>
                <Form.Item
                  label={<label className="fw-medium">Lead Source</label>}
                  name="leadSource"
                  className="mb15"
                >
                  <Input className="common-input" />
                </Form.Item>
              </Col>

              <Col span={24} xl={isNewLead ? 24 : 12}>
                <Form.Item
                  label={<label className="fw-medium">Lead Description</label>}
                  name="description"
                  className="mb15"
                >
                  <Input.TextArea rows={2} className="common-input" />
                </Form.Item>
              </Col>

              <Col span={24} xl={isNewLead ? 24 : 12}>
                <Form.Item
                  label={<label className="fw-medium">Lead Rating</label>}
                  name="leadRating"
                  className="mb15"
                >
                  <Select
                    showSearch
                    options={leadRatingList}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </div>
      {!!initialValues?.id && (
        <div
          style={{
            padding: "0px 24px 0px",
          }}
        >
          <CustomFields
            wtlCustomFields={wtlCustomFields}
            onChange={(name, value) => {
              const updatedValues = wtlCustomFields.map((d) =>
                d?.fieldName === name
                  ? { ...d, fieldName: name, fieldValue: value }
                  : d
              );
              form.setFieldsValue({
                wtlCustomFields: updatedValues,
              });
              setWtlCustomFields(updatedValues);
            }}
          />
        </div>
      )}
      {!initialValues?.id && (
        <div
          className="d-flex heading13px cursor-pointer pr20 justify-content-end text-theme mb20 mt10"
          onClick={() => setMoreInfo(!moreInfo)}
        >
          {moreInfo ? "View Less" : "View More"}
        </div>
      )}
      <div className="d-flex gap-20 pr20 mt20 mb30 justify-content-end">
        {!initialValues?.id && (
          <Button className="secondary-btn mb30" onClick={closeModal}>
            Cancel
          </Button>
        )}

        <Button htmlType="submit" type="primary" className="theme-btn">
          Save
        </Button>
      </div>
    </Form>
  );
};

export default LeadForm;
